import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_APPLICATION_CONTROL_RULES_GRID } from '../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  nameCellCustomRenderer,
  PREDEFINED_FILTERS_KEYS,
  statusCellCustomRenderer,
  targetsCellCustomRenderer,
} from './grid-config';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { getApplicationControlSearchableKeywords } from '../../../../utils/uiSettings/applicationControl/application-control.values';
import { GzApplicationControlRulesGridDataProvider } from './GzApplicationControlRulesGridDataProvider';
import {
  ApplicationControlStartRuleItem,
  ApplicationControlStartRuleItemFormValue,
} from '@nirvana/shared/dtos/types/policies/common';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzButtonWebComponent } from '../../../../../../../ui/gzButton';
import { GzApplicationControlRulesFormWebComponent } from '../../helpers/rulesItemForm';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { validateDuplicates } from '../utils/validators';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../ui/gzButton/types';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_APPLICATION_CONTROL_RULES_GRID)
export class GzApplicationControlRulesGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ApplicationControlStartRuleItem,
  ApplicationControlStartRuleItemFormValue,
  GzApplicationControlRulesGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.NAME]: nameCellCustomRenderer.bind(null, this.onRuleNameClick.bind(this)),
    [ColumnKeys.STATUS]: statusCellCustomRenderer,
    [ColumnKeys.TARGETS]: targetsCellCustomRenderer,
  };
  protected showHeaderCustomActionsInsideGrid = true;

  protected searchableKeywords = getApplicationControlSearchableKeywords();

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;

  protected hasPriorityColumn = true;
  protected hasMoveToTopAction = true;

  protected translations = {
    editRow: translateFn('Edit priority'),
    deleteRow: translateFn('Delete'),
    moveToTop: translateFn('Move to top'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  protected getDataProvider(): GzApplicationControlRulesGridDataProvider {
    return new GzApplicationControlRulesGridDataProvider(this.initialGridItems);
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}">${translateFn('No rules')}</gz-text>

      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        style="margin-top: 5px;"
        @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Adjust your filters or start adding rules.')}
      </gz-button>
    `;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button id="add-button" type="${ButtonTypes.OUTLINE}" @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Add')}
      </gz-button>

      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        ?disabled
        @click="${this.onDeleteSelectedItems.bind(this)}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: ApplicationControlStartRuleItemFormValue): ValidationResult {
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzApplicationControlRulesGridDataProvider).items
    );
  }

  protected validateGridItemBeforeEdit(
    oldItem: ApplicationControlStartRuleItemFormValue,
    newItem: ApplicationControlStartRuleItemFormValue
  ): ValidationResult {
    // skip if item name is not changed
    if (newItem.name === oldItem.name) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  private onAddButtonClick(): void {
    const maxPriority = (this.gridComponent.dataProvider as GzApplicationControlRulesGridDataProvider).items.length + 1;
    const addRuleCallback = (formValue: ApplicationControlStartRuleItemFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzApplicationControlRulesFormWebComponent(maxPriority),
      strings: { title: translateFn('Add rule'), confirm: translateFn('Add rule'), cancel: translateFn('Cancel') },
      confirmCallbackFn: addRuleCallback,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onRuleNameClick(item: ApplicationControlStartRuleItemFormValue): void {
    const maxPriority = (this.gridComponent.dataProvider as GzApplicationControlRulesGridDataProvider).items.length;
    const addRuleCallback = (formValue: ApplicationControlStartRuleItemFormValue): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzApplicationControlRulesFormWebComponent(maxPriority, item),
      strings: {
        title: translateFn('Edit rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
      },
      confirmCallbackFn: addRuleCallback,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }
}
